export type MappingParentFields = {
  belongs_to_id?: number | string | null
  mapping_parent?: number | string | null
  parent?: number | string | null
  setting_mapping_id?: number | string | null
  id?: number | string | null
  input_type?: string | null
}

export const toFiniteMappingId = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const id = Number(value)
  return Number.isFinite(id) ? id : null
}

/** Existing belongs_to_id first so current nests stay unchanged. */
export const getMappingParentId = (
  mapping: MappingParentFields | null | undefined
): number | null =>
  toFiniteMappingId(mapping?.belongs_to_id) ??
  toFiniteMappingId(mapping?.mapping_parent) ??
  toFiniteMappingId(mapping?.parent)

export const getMappingOwnId = (
  mapping: MappingParentFields | null | undefined
): number | null =>
  toFiniteMappingId(mapping?.setting_mapping_id) ?? toFiniteMappingId(mapping?.id)

export const isBooleanMapping = (mapping: MappingParentFields | null | undefined): boolean =>
  `${mapping?.input_type ?? ''}`.toUpperCase() === 'BOOLEAN'

export const isTruthyMappingParentValue = (value: unknown): boolean => {
  if (value === true || value === 1) {
    return true
  }

  if (value === null || value === undefined) {
    return false
  }

  return ['true', '1', 'yes', 'y', 'on'].includes(`${value}`.trim().toLowerCase())
}

export type MappingParentGroup<T> = {
  parentEntries: T[]
  childrenByParentId: Map<number, T[]>
}

export type MappingRenderEntry = {
  enrichedFieldMapping: any
  index: number
}

/**
 * Groups mapping rows for BOOLEAN parent toggles.
 * belongs_to_id keeps the existing hide-if-parent rule.
 * mapping_parent / parent nest when that id is a BOOLEAN row in this list.
 */
export const groupMappingsByParent = <T,>(
  entries: T[],
  getMapping: (entry: T) => MappingParentFields | null | undefined
): MappingParentGroup<T> => {
  const booleanParentIds = new Set<number>()

  entries.forEach((entry) => {
    const mapping = getMapping(entry)
    const ownId = getMappingOwnId(mapping)
    if (ownId !== null && isBooleanMapping(mapping)) {
      booleanParentIds.add(ownId)
    }
  })

  const childrenByParentId = new Map<number, T[]>()
  const parentEntries: T[] = []

  entries.forEach((entry) => {
    const mapping = getMapping(entry)
    const belongsToId = toFiniteMappingId(mapping?.belongs_to_id)

    if (belongsToId !== null) {
      const existing = childrenByParentId.get(belongsToId) ?? []
      childrenByParentId.set(belongsToId, [...existing, entry])
      return
    }

    const declaredParentId =
      toFiniteMappingId(mapping?.mapping_parent) ?? toFiniteMappingId(mapping?.parent)

    if (declaredParentId !== null && booleanParentIds.has(declaredParentId)) {
      const existing = childrenByParentId.get(declaredParentId) ?? []
      childrenByParentId.set(declaredParentId, [...existing, entry])
      return
    }

    parentEntries.push(entry)
  })

  return {parentEntries, childrenByParentId}
}

export const groupMappingRenderEntries = (
  entries: MappingRenderEntry[]
): MappingParentGroup<MappingRenderEntry> =>
  groupMappingsByParent(entries, (entry) => entry.enrichedFieldMapping)

export const getBooleanMappingChildren = <T,>(
  mapping: MappingParentFields | null | undefined,
  childrenByParentId: Map<number, T[]>
): T[] => {
  if (!isBooleanMapping(mapping)) {
    return []
  }

  const ownId = getMappingOwnId(mapping)
  if (ownId === null) {
    return []
  }

  return childrenByParentId.get(ownId) ?? []
}
